import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrderNotifications } from '../hooks/useOrderNotifications';
import { api } from '../api';
import './OrderHistoryPage.css';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleString('en-AU', { month: 'short' });
  const time = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day} ${month} · ${time}`;
}

function statusBadgeClass(status) {
  switch (status) {
    case 'pending': return 'badge-pending';
    case 'preparing': return 'badge-preparing';
    case 'ready': return 'badge-ready';
    case 'collected': return 'badge-collected';
    case 'cancelled': return 'badge-cancelled';
    default: return '';
  }
}

export default function OrderHistoryPage() {
  const { user, isLoggedIn, logout, updateProfile, deleteAccount } = useAuth();
  const { markAllSeen } = useOrderNotifications();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [savedCards, setSavedCards] = useState([]);
  const [removingCardId, setRemovingCardId] = useState('');

  useEffect(() => {
    if (!isLoggedIn) return;
    api.getMyOrders()
      .then(setOrders)
      .catch(() => {})
      .finally(() => setLoading(false));
    api.getPayMethods()
      .then((rows) => setSavedCards(Array.isArray(rows) ? rows : []))
      .catch(() => setSavedCards([]));
    markAllSeen();
  }, [isLoggedIn, markAllSeen]);

  async function handleRemoveCard(payMethodId) {
    if (!window.confirm('Remove this saved card? You can add it again at checkout.')) return;
    setRemovingCardId(payMethodId);
    try {
      await api.deletePayMethod(payMethodId);
      setSavedCards((prev) => prev.filter((c) => c.pay_method_id !== payMethodId));
    } catch (err) {
      alert(err?.message || 'Could not remove the saved card.');
    } finally {
      setRemovingCardId('');
    }
  }

  if (!isLoggedIn) {
    navigate('/auth?return=/my-orders', { replace: true });
    return null;
  }

  async function handleNameSave() {
    if (!newName.trim()) return;
    try {
      await updateProfile({ name: newName.trim() });
      setEditingName(false);
    } catch { /* ignore */ }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete account. Please try again.');
      setDeleting(false);
    }
  }

  return (
    <main className="order-history container">
      <div className="oh-profile card">
        <div className="oh-profile-header">
          <div className="oh-avatar">{user?.name?.charAt(0)?.toUpperCase() || '?'}</div>
          <div className="oh-profile-info">
            {editingName ? (
              <div className="oh-edit-name">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Your name"
                  autoFocus
                />
                <button className="btn btn-sm btn-primary" onClick={handleNameSave}>Save</button>
                <button className="btn btn-sm btn-outline" onClick={() => setEditingName(false)}>Cancel</button>
              </div>
            ) : (
              <h2 className="oh-name">
                {user?.name}
                <button
                  className="oh-edit-btn"
                  onClick={() => { setNewName(user?.name || ''); setEditingName(true); }}
                  title="Edit name"
                >
                  &#9998;
                </button>
              </h2>
            )}
            <p className="oh-email">{user?.email}</p>
          </div>
        </div>
        <button className="btn btn-sm btn-outline oh-logout" onClick={() => { logout(); navigate('/'); }}>
          Sign Out
        </button>
      </div>

      {savedCards.length > 0 && (
        <section className="oh-saved-cards card" aria-labelledby="oh-cards-heading">
          <h2 id="oh-cards-heading" className="oh-section-title" style={{ marginBottom: 8 }}>Saved card</h2>
          <p className="oh-saved-cards-hint">
            Card numbers are held by Tyro. We only store a token and the last four digits.
          </p>
          {savedCards.map((card) => (
            <div key={card.pay_method_id} className="oh-saved-card-row">
              <span>{card.label}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={removingCardId === card.pay_method_id}
                onClick={() => handleRemoveCard(card.pay_method_id)}
              >
                {removingCardId === card.pay_method_id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </section>
      )}

      <h2 className="oh-section-title">Order History</h2>

      {loading ? (
        <div className="page-center" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
          <p>Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="oh-empty card">
          <p>No orders yet. Place your first order from the menu!</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>Browse Menu</button>
        </div>
      ) : (
        <div className="oh-list">
          {orders.map((order) => (
            <div key={order.id} className="oh-order card">
              <div className="oh-order-header">
                <span className={`badge ${statusBadgeClass(order.status)}`}>{order.status}</span>
                <span className="oh-order-date">{formatDate(order.created_at)}</span>
              </div>
              <div className="oh-order-items">
                {(order.items || []).map((item) => (
                  <div key={item.id} className="oh-order-item">
                    <span className="oh-item-qty">{item.quantity}x</span>
                    <span className="oh-item-name">{item.item_name}</span>
                    {item.options && (() => {
                      const opts = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                      const parts = Object.entries(opts).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`);
                      return parts.length > 0 ? <span className="oh-item-opts">{parts.join(' · ')}</span> : null;
                    })()}
                  </div>
                ))}
              </div>
              <div className="oh-order-footer">
                <span className="oh-order-total">${Number(order.total).toFixed(2)}</span>
                <span className="oh-order-venue">{order.venue}</span>
              </div>
              <button
                className="btn btn-sm btn-outline oh-view-order"
                onClick={() => {
                  const path = order.order_group
                    ? `/confirmation/group/${order.order_group}`
                    : `/confirmation/${order.order_number}`;
                  navigate(path);
                }}
              >
                View Order
              </button>
            </div>
          ))}
        </div>
      )}

      <section className="oh-danger-zone card" aria-labelledby="oh-delete-heading">
        <h2 id="oh-delete-heading" className="oh-danger-title">Delete Account</h2>
        <p className="oh-danger-text">
          Permanently delete your St Barb Kitchen account and personal details
          (name, email and sign-in). Past orders will be kept for our records but
          will no longer be linked to you. This cannot be undone.
        </p>

        {!confirmDelete ? (
          <button
            type="button"
            className="btn btn-danger oh-delete-btn"
            onClick={() => { setDeleteError(''); setConfirmDelete(true); }}
          >
            Delete My Account
          </button>
        ) : (
          <div className="oh-delete-confirm">
            <p className="oh-delete-confirm-text">
              Are you sure? This will permanently delete your account and sign you out.
            </p>
            {deleteError && <p className="oh-delete-error">{deleteError}</p>}
            <div className="oh-delete-actions">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setConfirmDelete(false); setDeleteError(''); }}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
